/**
 * ProShip Direct — the email code in front of every payment a merchant starts.
 *
 * One hook for the four places that charge: buying a plan, accepting an offer, upgrading and
 * paying a renewal that failed. It runs on ProShip's own OTP framework: `send` mails the code and
 * keeps the tokenId it returns; `proof` hands { otpTokenId, otp } to the paying call, which checks
 * the code on the server before it charges. There is no separate verify step, so a wrong code is
 * reported by the paying call, and the caller shows that message under the code box.
 *
 * The local challenge from activationOtp.js drives only the countdown and the resend throttle.
 */
import { useCallback, useState } from 'react';
import { getUserName } from '../../../helpers/ReduxHelpers';
import { newChallenge, OTP_LENGTH, OTP_MESSAGES } from './activationOtp';
import { errText, sendPaymentOtp } from './api';

export default function useActivationOtp() {
  const email = getUserName() || '';
  const [challenge, setChallenge] = useState(null);
  const [tokenId, setTokenId] = useState(null);
  const [entered, setEntered] = useState('');
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);

  /** Mail a code. Resolves true when it went; on failure the reason is in `error`. */
  const send = useCallback(async () => {
    setSending(true);
    setError(null);
    setEntered('');
    try {
      const res = await sendPaymentOtp();
      setTokenId(res?.data?.tokenId || null);
      setChallenge((c) => newChallenge(email, { attempts: c?.attempts || 0 }));
      return true;
    } catch (e) {
      setError(errText(e));
      return false;
    } finally {
      setSending(false);
    }
  }, [email]);

  /** What the paying call carries, or null (with the reason in `error`) when the code is incomplete. */
  const proof = useCallback(() => {
    if (entered.length !== OTP_LENGTH || !tokenId) {
      setError(tokenId ? OTP_MESSAGES.INCOMPLETE : OTP_MESSAGES.NO_CHALLENGE);
      return null;
    }
    return { otpTokenId: tokenId, otp: entered };
  }, [entered, tokenId]);

  const onChange = useCallback((value) => {
    setEntered(value);
    // Clearing on edit, so a stale error does not sit under a code they are still typing.
    setError(null);
  }, []);

  /** Props for ActivationOtpStep, in one spread. */
  const stepProps = {
    challenge,
    email,
    entered,
    onChange,
    serverMode: true,
    errorText: error,
    sending,
    onResend: send,
  };

  return { email, challenge, entered, error, setError, sending, send, proof, stepProps };
}
